package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.draw.rotate
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.agent.AzioneProposta
import com.heelion.farmafreelance.models.comeCreatore
import com.heelion.farmafreelance.models.CreatoreTipo
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.Notification
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.voice.VoiceIntent

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NotificationScreen(
    viewModel: AuthViewModel,
    onBack: () -> Unit,
    /** Comandi di Farmy che portano in un'altra sezione (es. "apri la Home"). */
    onIntent: (rawText: String, intent: VoiceIntent) -> Unit = { _, _ -> }
) {
    val notifications = viewModel.notifications
    val esecutore = LocalAssistente.current.esecutore
    // Proposta su cui è stato chiesto aiuto a Farmy (popup aperto).
    var farmySu by remember { mutableStateOf<Match?>(null) }

    // Segna come lette all'apertura e ricarica le proposte: i pulsanti dipendono dal loro stato attuale.
    LaunchedEffect(Unit) {
        viewModel.markNotificationsAsRead()
        viewModel.refreshUserData()
    }

    farmySu?.let { m ->
        if (esecutore != null) {
            DialogAssistente(
                onIntent = onIntent,
                onDismiss = { farmySu = null },
                argomento = "Aiutami con questa proposta" to suspend { esecutore.spiegaProposta(m) }
            )
        }
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("Notifiche") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(AppIcons.ArrowBack, contentDescription = "Back")
                    }
                }
            )
        }
    ) { padding ->
        // Proposte che aspettano una mia risposta, dallo stato attuale (non dalle notifiche): così si
        // rispondono anche quelle arrivate con notifiche vecchie, non collegate al turno.
        // Esclusi i suggerimenti automatici ("smart-…"): sono nella Home, non proposte di qualcuno.
        val mio = viewModel.currentUserRole?.comeCreatore
        val daGestire = if (mio == null) emptyList() else viewModel.currentMatches.filter { m ->
            m.id?.startsWith("smart-") == false &&
                AzioneProposta.possibili(m, mio).any { it == AzioneProposta.ACCETTA || it == AzioneProposta.CONFERMA }
        }.sortedBy { it.dataTurno }
        val collegateANotifica = notifications.mapNotNull { it.matchId }.toSet()
        val soloInCima = daGestire.filter { it.id !in collegateANotifica }

        if (notifications.isEmpty() && soloInCima.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Icon(AppIcons.Notifications, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(64.dp))
                    Spacer(Modifier.height(16.dp))
                    Text("Nessuna nuova notifica", color = Color.Gray)
                }
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (soloInCima.isNotEmpty()) {
                    item {
                        Text("Da gestire", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    }
                    items(soloInCima, key = { "match-${it.id}" }) { m ->
                        PropostaDaGestire(m, viewModel, onChiediFarmy = if (esecutore != null) { x -> farmySu = x } else null)
                    }
                    if (notifications.isNotEmpty()) {
                        item { Text("Notifiche", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold) }
                    }
                }
                items(notifications) { notification ->
                    NotificationItem(notification, viewModel, onChiediFarmy = if (esecutore != null) { m -> farmySu = m } else null)
                }
            }
        }
    }
}

@Composable
fun NotificationItem(notification: Notification, viewModel: AuthViewModel, onChiediFarmy: ((Match) -> Unit)? = null) {
    var showDeleteConfirm by remember { mutableStateOf(false) }
    // La proposta di cui parla la notifica, com'è adesso (può essere cambiata dopo la notifica).
    val match = notification.matchId?.let { id -> viewModel.currentMatches.firstOrNull { it.id == id } }

    if (showDeleteConfirm) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text("Elimina Notifica") },
            text = { Text("Vuoi davvero eliminare questa notifica?") },
            confirmButton = {
                Button(
                    onClick = {
                        viewModel.deleteNotification(notification.id ?: "")
                        showDeleteConfirm = false
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Red)
                ) {
                    Text("ELIMINA")
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirm = false }) {
                    Text("ANNULLA")
                }
            }
        )
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (notification.isRead) Color.White else Color(0xFFF1F8E9)
        )
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            val (icon, color) = when(notification.tipo) {
                "CONFIRM" -> AppIcons.Check to Color(0xFF2E7D32)
                "CANCEL" -> AppIcons.Delete to Color(0xFFC62828)
                else -> AppIcons.Info to MaterialTheme.colorScheme.primary
            }
            
            Icon(icon, contentDescription = null, tint = color, modifier = Modifier.size(24.dp))
            Spacer(Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text(notification.titolo, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                Text(notification.messaggio, style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                if (match != null) AzioniProposta(match, viewModel, onChiediFarmy)
            }
            
            IconButton(onClick = { showDeleteConfirm = true }) {
                Icon(AppIcons.Delete, contentDescription = "Delete", tint = Color.LightGray.copy(alpha = 0.5f), modifier = Modifier.size(18.dp))
            }
        }
    }
}

/** Una proposta che aspetta una mia risposta (sezione "Da gestire"): chi, quando e i pulsanti. */
@Composable
private fun PropostaDaGestire(match: Match, viewModel: AuthViewModel, onChiediFarmy: ((Match) -> Unit)?) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF8E1))
    ) {
        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.Top) {
            Icon(AppIcons.Info, contentDescription = null, tint = Color(0xFFF57C00), modifier = Modifier.size(24.dp))
            Spacer(Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                val titolo = if (match.stato == MatchStato.ACCETTATO) "Da confermare" else "Proposta da valutare"
                Text("$titolo: ${match.nomeControparte ?: "turno"}", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                match.messaggio?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = Color.Gray) }
                AzioniProposta(match, viewModel, onChiediFarmy)
            }
        }
    }
}

/**
 * Sotto una notifica legata a una proposta: i pulsanti delle risposte possibili adesso (gli stessi
 * della Home), oppure lo stato se non c'è più niente da fare, e "Chiedi a Farmy".
 * Le risposte che annullano (rifiuta, ritira, disdici) chiedono prima conferma.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun AzioniProposta(match: Match, viewModel: AuthViewModel, onChiediFarmy: ((Match) -> Unit)?) {
    val mio = viewModel.currentUserRole?.comeCreatore ?: return
    var inCorso by remember(match.id, match.stato) { mutableStateOf(false) }
    var errore by remember(match.id, match.stato) { mutableStateOf<String?>(null) }
    var daConfermare by remember { mutableStateOf<AzioneProposta?>(null) }

    fun esegui(a: AzioneProposta) {
        inCorso = true
        errore = null
        viewModel.respondToMatch(match.id ?: return, a.codice) { ok ->
            inCorso = false
            if (!ok) errore = viewModel.actionErrorMessage ?: "Non ci sono riuscito, riprova."
        }
    }

    daConfermare?.let { a ->
        AlertDialog(
            onDismissRequest = { daConfermare = null },
            title = { Text("${a.etichetta}?") },
            text = { Text("Vuoi davvero ${a.verbo} il turno con ${match.nomeControparte ?: "la controparte"}? Riceverà una notifica.") },
            confirmButton = { Button(onClick = { daConfermare = null; esegui(a) }) { Text(a.etichetta.uppercase()) } },
            dismissButton = { TextButton(onClick = { daConfermare = null }) { Text("ANNULLA") } }
        )
    }

    val azioni = AzioneProposta.possibili(match, mio)
    Spacer(Modifier.height(8.dp))
    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        if (azioni.isEmpty()) {
            val stato = when (match.stato) {
                MatchStato.CONFERMATO -> "Turno confermato"
                MatchStato.COMPLETATO -> "Turno svolto"
                MatchStato.RIFIUTATO, MatchStato.ANNULLATO -> "Proposta annullata"
                else -> "In attesa di risposta"
            }
            Text(stato, style = MaterialTheme.typography.labelLarge, color = Color(0xFF2E7D32), modifier = Modifier.align(Alignment.CenterVertically))
        }
        azioni.forEach { a ->
            if (a.annulla) {
                OutlinedButton(onClick = { daConfermare = a }, enabled = !inCorso) { Text(a.etichetta) }
            } else {
                Button(onClick = { esegui(a) }, enabled = !inCorso) { Text(a.etichetta) }
            }
        }
        if (onChiediFarmy != null) {
            TextButton(onClick = { onChiediFarmy(match) }) { Text("Chiedi a Farmy") }
        }
    }
    errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
}
